import { type ComponentType, type FC, type SVGProps, useState } from 'react';

import {
  DocumentIcon,
  DocumentTextIcon,
  FolderIcon,
  LinkIcon,
  MusicalNoteIcon,
  PhotoIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { LockClosedIcon } from '@heroicons/react/24/solid';

import { cn } from '../utils/cn';

const FORMAT_ICONS: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  pdf: DocumentTextIcon,
  png: PhotoIcon,
  jpeg: PhotoIcon,
  gif: PhotoIcon,
  webp: PhotoIcon,
  mp3: MusicalNoteIcon,
  zip: FolderIcon,
  xlsx: TableCellsIcon,
  csv: TableCellsIcon,
};

export interface CommunityResourceThumbnailProps {
  fileFormat: string | null;
  url: string | null;
  size: 'xs' | 'sm' | 'md';
  locked: boolean;
  lockedLabel: string;
  className?: string;
}

export const CommunityResourceThumbnail: FC<CommunityResourceThumbnailProps> = ({
  fileFormat,
  url,
  size,
  locked,
  lockedLabel,
  className,
}) => {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const Icon = fileFormat ? (FORMAT_ICONS[fileFormat] ?? DocumentIcon) : LinkIcon;

  return (
    <div className={cn('relative shrink-0 overflow-hidden bg-black/[0.04]', className)}>
      {url && url !== failedUrl ? (
        <img
          src={url}
          alt=""
          referrerPolicy="no-referrer"
          className="size-full object-cover"
          onError={() => setFailedUrl(url)}
        />
      ) : (
        <div aria-hidden="true" className="flex size-full items-center justify-center">
          {(size !== 'xs' || !fileFormat) && <Icon className="size-1/3 max-h-16 max-w-16 text-black/40" />}
          {fileFormat && (
            <span
              className={cn('rounded-sm px-1 font-bold leading-tight text-white', {
                'text-[8px]': size === 'xs',
                'absolute bottom-1.5 left-1.5 text-[8px]': size === 'sm',
                'absolute bottom-3 left-3 text-[11px]': size === 'md',
                'bg-red-500': fileFormat === 'pdf',
                'bg-emerald-600': fileFormat === 'xlsx' || fileFormat === 'csv',
                'bg-indigo-600': fileFormat === 'md',
                'bg-gray-600': !['pdf', 'xlsx', 'csv', 'md'].includes(fileFormat),
              })}
            >
              {fileFormat.toUpperCase()}
            </span>
          )}
        </div>
      )}
      {locked && (
        <span
          role="img"
          aria-label={lockedLabel}
          className={cn(
            'absolute z-10 flex items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm',
            {
              'right-0.5 top-0.5 size-4': size === 'xs',
              'right-1.5 top-1.5 size-6': size === 'sm',
              'right-3 top-3 size-8': size === 'md',
            },
          )}
        >
          <LockClosedIcon
            aria-hidden="true"
            className={cn({ 'size-2.5': size === 'xs', 'size-3': size === 'sm', 'size-4': size === 'md' })}
          />
        </span>
      )}
    </div>
  );
};
